import { useCallback, useRef, useState } from 'react';

export type ClassValue = string | false | null | undefined | 0;

/** Join class names, skipping falsy values. */
export function cx(...parts: ClassValue[]): string {
  let out = '';
  for (const p of parts) if (p) out += (out ? ' ' : '') + p;
  return out;
}

/** State that can be controlled (`value` + `onChange`) or uncontrolled (`defaultValue`). */
export function useControllableState<T>(value: T | undefined, defaultValue: T, onChange?: (v: T) => void): [T, (v: T) => void] {
  const [inner, setInner] = useState(defaultValue);
  const controlled = value !== undefined;
  const current = controlled ? value : inner;
  const cb = useRef(onChange);
  cb.current = onChange;
  const set = useCallback(
    (v: T) => {
      if (!controlled) setInner(v);
      cb.current?.(v);
    },
    [controlled],
  );
  return [current, set];
}

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

const KEY_LABELS: Record<string, [mac: string, other: string]> = {
  mod: ['⌘', 'Ctrl'],
  cmd: ['⌘', 'Ctrl'],
  ctrl: ['⌃', 'Ctrl'],
  alt: ['⌥', 'Alt'],
  shift: ['⇧', 'Shift'],
  enter: ['↵', 'Enter'],
  backspace: ['⌫', 'Backspace'],
  delete: ['⌦', 'Del'],
  escape: ['Esc', 'Esc'],
  esc: ['Esc', 'Esc'],
  up: ['↑', '↑'],
  down: ['↓', '↓'],
  left: ['←', '←'],
  right: ['→', '→'],
  space: ['Space', 'Space'],
  tab: ['Tab', 'Tab'],
};

/** `'mod+shift+k'` → `['⌘','⇧','K']` on macOS, `['Ctrl','Shift','K']` elsewhere. */
export function shortcutKeys(shortcut: string): string[] {
  return shortcut
    .split('+')
    .map((k) => k.trim().toLowerCase())
    .filter(Boolean)
    .map((k) => KEY_LABELS[k]?.[isMac ? 0 : 1] ?? (k.length === 1 ? k.toUpperCase() : k[0]!.toUpperCase() + k.slice(1)));
}

export function formatShortcut(shortcut: string): string {
  return shortcutKeys(shortcut).join(isMac ? '' : '+');
}

/** Does a keyboard event match `'mod+k'`-style shortcut? */
export function matchShortcut(e: KeyboardEvent | React.KeyboardEvent, shortcut: string): boolean {
  const parts = shortcut.toLowerCase().split('+');
  const key = parts.pop()!;
  const want = new Set(parts);
  const mod = isMac ? e.metaKey : e.ctrlKey;
  if (want.has('mod') !== mod) return false;
  if (want.has('shift') !== e.shiftKey) return false;
  if (want.has('alt') !== e.altKey) return false;
  if (!want.has('mod') && (isMac ? e.metaKey : e.ctrlKey)) return false;
  const k = e.key.toLowerCase();
  // Arrows are named like their labels ('up'), while events call them 'ArrowUp'.
  return k === key || k === `arrow${key}` || (key === 'esc' && k === 'escape') || (key === 'space' && k === ' ');
}

/** Is the event target an editable element (so global shortcuts should back off)? */
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || !el.tagName) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}
